import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import mapboxgl from 'mapbox-gl';
import { useAtom, useAtomValue } from 'jotai';
import { ZCDButton } from '@zencity/common-ui';
import { get } from 'underscore';
import {
  GEOJSON_FEATURE,
  GEOJSON_FEATURE_COLLECTION,
  GEOJSON_POINT,
  GEOJSON_POLYGON,
} from 'admin/engagement/components/GeolocationQuestionInput/helper';
import { useMapboxMap, registerCustomPinImages } from '../../hooks';
import { iconImageForInput, iconImageForMapLayer } from '../../markers';
import PinIcon from '../../PinIcon';
import { drawerStateAtom, mapCamerasAtom } from '../../store';
import EmptyResults from '../EmptyResults';
import PinDropDataTables from './PinDropDataTable';

function GeolocationQuestionResults(props) {
  const { question, hasResponses } = props;
  const { data, showDataTable } = question;
  const {
    metadata,
    inputs,
    mapLayers,
    coordinates,
    pinOptionsCommentStatistic,
    pinOptionsSentimentCounts,
    mapLayersCommentStatistic,
    mapLayersSentimentCounts,
    overAllCommentStatistic,
    overAllSentimentCounts,
  } = data;
  const container = useRef(null);
  const savedCamera = useRef(null);
  const hasFittedRef = useRef(false);
  const [mapCameras, setMapCameras] = useAtom(mapCamerasAtom);
  const [selectedCoordinate, setSelectedCoordinate] = useState(null);
  const isEmpty = coordinates.length === 0;
  const map = useMapboxMap(container, {
    enabled: !isEmpty,
    center: mapCameras[question.uuid]?.center ?? data.center ?? coordinates[0],
    zoom: mapCameras[question.uuid]?.zoom ?? data.zoom ?? 14,
  });
  const drawerState = useAtomValue(drawerStateAtom);

  useEffect(() => {
    if (!map) return;
    if (isEmpty) return;

    const sourceData = {
      type: GEOJSON_FEATURE_COLLECTION,
      features: coordinates.map((point) => ({
        type: GEOJSON_FEATURE,
        properties: {
          comment: point.comment || undefined,
          inputId: point.inputId,
        },
        geometry: {
          type: GEOJSON_POINT,
          coordinates: [point.lng, point.lat],
        },
      })),
    };

    const source = map.getSource('responses');

    if (source) {
      source.setData(sourceData);
    } else {
      map.addSource('responses', {
        data: sourceData,
        type: 'geojson',
        cluster: true,
        clusterMaxZoom: 14,
        clusterRadius: 50,
      });
    }

    // Add source for each map layer
    mapLayers?.forEach((layer) => {
      if (map.getSource(`mapLayers-${layer.id}`)) return;
      map.addSource(`mapLayers-${layer.id}`, {
        data: layer.geojson,
        type: 'geojson',
      });
    });

    // Fit the map to the pin coordinates once, unless the user already
    // panned/zoomed (mapCameras has a saved camera for this question).
    if (!hasFittedRef.current && !mapCameras[question.uuid] && coordinates.length > 0) {
      const bounds = coordinates.reduce(
        (acc, point) => acc.extend([point.lng, point.lat]),
        new mapboxgl.LngLatBounds()
      );
      map.fitBounds(bounds, { padding: 60, maxZoom: 15, duration: 0 });
      hasFittedRef.current = true;
    }
  }, [map, isEmpty, coordinates, mapLayers]);

  useEffect(() => {
    if (!map) return;
    if (isEmpty) return;

    // Register lazy custom-SVG images before the symbol layers reference them (answer pins + POIs).
    registerCustomPinImages(map, inputs, 'input');
    registerCustomPinImages(map, mapLayers, 'mapLayer');

    map.addLayer({
      id: 'clusters',
      type: 'circle',
      source: 'responses',
      filter: ['has', 'point_count'],
      paint: {
        'circle-color': ['step', ['get', 'point_count'], '#19803C', 100, '#f1f075', 750, '#f28cb1'],
        'circle-radius': ['step', ['get', 'point_count'], 18, 100, 24, 750, 30],
      },
    });

    map.addLayer({
      id: 'cluster-count',
      type: 'symbol',
      source: 'responses',
      filter: ['has', 'point_count'],
      layout: {
        'text-field': ['get', 'point_count_abbreviated'],
        'text-font': ['Arial Unicode MS Bold'],
        'text-size': 14,
      },
      paint: {
        'text-color': '#ffffff',
      },
    });

    // Pin options
    if (inputs) {
      inputs?.forEach((input) => {
        const pinWithoutCommentId = `pin-${input.id}`;
        const pinWithCommentId = `pin-${input.id}-comment`;

        const layerOptions = {
          id: `pin-${input.id}`,
          type: 'symbol',
          source: 'responses',
          layout: {
            'icon-image': iconImageForInput(input.metadata, input.id),
            'icon-size': 1,
            'icon-allow-overlap': true,
            'icon-offset': [0, 0],
            'icon-anchor': 'bottom',
            'text-field': ['get', 'title'],
            'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
            'text-offset': [0, 0],
            'text-anchor': 'bottom',
          },
        };

        map.addLayer({
          ...layerOptions,
          id: pinWithoutCommentId,
          filter: ['all', ['==', 'inputId', input.id], ['!has', 'comment']],
        });

        map.addLayer({
          ...layerOptions,
          id: pinWithCommentId,
          filter: ['all', ['==', 'inputId', input.id], ['has', 'comment']],
        });

        // eslint-disable-next-line max-nested-callbacks
        map.on('click', pinWithCommentId, (event) => {
          const feature = event.features[0];
          const lngLat = feature.geometry.coordinates;
          const { comment } = feature.properties;
          new mapboxgl.Popup({ anchor: 'top' }).setLngLat(lngLat).setHTML(comment).addTo(map);
        });

        map.on('mouseenter', [pinWithCommentId], () => {
          map.getCanvas().style.cursor = 'pointer';
        });

        map.on('mouseleave', [pinWithCommentId], () => {
          map.getCanvas().style.cursor = '';
        });
      });
    } else {
      const pinWithoutCommentId = `pins`;
      const pinWithCommentId = `pins-comment`;

      const layerOptions = {
        id: `pins`,
        type: 'symbol',
        source: 'responses',
        layout: {
          'icon-image': 'markerRed',
          'icon-size': 1,
          'icon-allow-overlap': true,
          'icon-offset': [0, 0],
          'icon-anchor': 'bottom',
          'text-field': ['get', 'title'],
          'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
          'text-offset': [0, 0],
          'text-anchor': 'bottom',
        },
      };

      map.addLayer({
        ...layerOptions,
        id: pinWithoutCommentId,
        filter: ['all', ['!has', 'comment']],
      });

      map.addLayer({
        ...layerOptions,
        id: pinWithCommentId,
        filter: ['all', ['has', 'comment']],
      });

      // eslint-disable-next-line max-nested-callbacks
      map.on('click', pinWithCommentId, (event) => {
        const feature = event.features[0];
        const lngLat = feature.geometry.coordinates;
        const { comment } = feature.properties;
        new mapboxgl.Popup({ anchor: 'top' }).setLngLat(lngLat).setHTML(comment).addTo(map);
      });

      map.on('mouseenter', [pinWithCommentId], () => {
        map.getCanvas().style.cursor = 'pointer';
      });

      map.on('mouseleave', [pinWithCommentId], () => {
        map.getCanvas().style.cursor = '';
      });
    }

    // Add map layers
    mapLayers?.forEach((layer) => {
      const mapLayerSourceId = `mapLayers-${layer.id}`;
      const polygonFillLayerId = `mapLayers-${layer.id}`;
      const polygonOutlineLayerId = `mapLayers-${layer.id}-outline`;
      const poiLayerId = `mapLayers-${layer.id}-point`;

      if (get(layer.geojson, 'type') === GEOJSON_POLYGON) {
        // Add fill layer for polygons
        map.addLayer({
          id: polygonFillLayerId,
          type: 'fill',
          source: mapLayerSourceId,
          paint: {
            'fill-color': '#DB3771',
            'fill-opacity': 0.3,
          },
        });

        // Add outline layer for polygons
        map.addLayer({
          id: polygonOutlineLayerId,
          type: 'line',
          source: mapLayerSourceId,
          paint: {
            'line-color': '#DB3771',
            'line-width': 2,
          },
        });
      }

      if (get(layer.geojson, 'type') === GEOJSON_POINT) {
        // Add layers for points of interest
        map.addLayer({
          id: poiLayerId,
          type: 'symbol',
          source: mapLayerSourceId,
          filter: ['==', '$type', GEOJSON_POINT],
          layout: {
            'icon-image': iconImageForMapLayer(layer.metadata, layer.id),
            'icon-size': 0.8,
            'icon-allow-overlap': true,
            'icon-offset': [0, 0],
            'icon-anchor': 'bottom',
          },
        });

        // eslint-disable-next-line max-nested-callbacks
        map.on('click', poiLayerId, (event) => {
          const feature = event.features;
          const lngLat = feature.geometry.coordinates;
          new mapboxgl.Popup({ anchor: 'top' }).setLngLat(lngLat).setHTML(layer.label).addTo(map);
        });

        map.on('mouseenter', [poiLayerId], () => {
          map.getCanvas().style.cursor = 'pointer';
        });

        map.on('mouseleave', [poiLayerId], () => {
          map.getCanvas().style.cursor = '';
        });
      }
    });

    map.on('click', 'clusters', (event) => {
      const features = map.queryRenderedFeatures(event.point, { layers: ['clusters'] });
      const clusterId = features[0].properties?.cluster_id;
      // eslint-disable-next-line max-nested-callbacks
      map.getSource('responses').getClusterExpansionZoom(clusterId, (error, zoom) => {
        if (error) return;
        map.easeTo({ center: features[0].geometry.coordinates, zoom });
      });
    });

    map.on('click', 'points-with-comment', (event) => {
      const feature = event.features[0];
      const lngLat = feature.geometry.coordinates;
      const { comment } = feature.properties;
      new mapboxgl.Popup({ anchor: 'top' }).setLngLat(lngLat).setHTML(comment).addTo(map);
    });

    map.on('mouseenter', ['clusters', 'points-with-comment'], () => {
      map.getCanvas().style.cursor = 'pointer';
    });

    map.on('mouseleave', ['clusters', 'points-with-comment'], () => {
      map.getCanvas().style.cursor = '';
    });

    const persistCamera = () => {
      setMapCameras((cameras) => ({
        ...cameras,
        [question.uuid]: {
          center: map.getCenter().toArray(),
          zoom: map.getZoom(),
        },
      }));
    };

    map.on('moveend', persistCamera);
    map.on('zoomend', persistCamera);
  }, [map, isEmpty]);

  useEffect(() => {
    if (map && selectedCoordinate)
      map.flyTo({ center: [selectedCoordinate.lng, selectedCoordinate.lat], zoom: 15 });
  }, [map, selectedCoordinate]);

  useEffect(() => {
    map?.resize();
  }, [drawerState]);

  // Prefix each pin-option table row's label with its marker (custom SVG or preset color) so the
  // table doubles as the pin legend. `label` becomes a node, so the table columns render it via a
  // cell renderer (see PinOptionDataTable(WithSentimentData)).
  const inputsKeyedById = (inputs || []).reduce((acc, input) => {
    acc[input.id] = input;
    return acc;
  }, {});
  const pinOptionsSentimentCountsWithIcon = pinOptionsSentimentCounts?.map((pin) => ({
    ...pin,
    label: (
      <span className="pin-option-label">
        <PinIcon metadata={inputsKeyedById[pin.inputId]?.metadata} />
        {pin.label}
      </span>
    ),
  }));

  function renderContent() {
    if (isEmpty)
      return (
        <div style={{ padding: '2rem' }}>
          <EmptyResults hasResponses={hasResponses} />
        </div>
      );

    return (
      <>
        <div ref={container} className="engage-results-map">
          {selectedCoordinate && (
            <div className="engage-results-map__return">
              <ZCDButton
                text="Return"
                variant="secondary"
                preIcon="undo"
                onClick={() => {
                  map?.easeTo(savedCamera.current);
                  savedCamera.current = null;
                  setSelectedCoordinate(null);
                }}
              />
            </div>
          )}
        </div>

        {showDataTable && (
          <div>
            <PinDropDataTables
              metadata={metadata}
              inputs={inputs}
              mapLayers={mapLayers}
              pinOptionsCommentStatistic={pinOptionsCommentStatistic}
              pinOptionsSentimentCounts={pinOptionsSentimentCountsWithIcon}
              mapLayersCommentStatistic={mapLayersCommentStatistic}
              mapLayersSentimentCounts={mapLayersSentimentCounts}
              overAllCommentStatistic={overAllCommentStatistic}
              overAllSentimentCounts={overAllSentimentCounts}
            />
          </div>
        )}
      </>
    );
  }

  return <div>{renderContent()}</div>;
}

GeolocationQuestionResults.propTypes = {
  question: PropTypes.shape({
    uuid: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    showDataTable: PropTypes.bool,
    data: PropTypes.shape({
      center: PropTypes.shape({
        lat: PropTypes.number.isRequired,
        lng: PropTypes.number.isRequired,
      }),
      zoom: PropTypes.number,
      metadata: PropTypes.shape({}),
      inputs: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string.isRequired,
          label: PropTypes.string.isRequired,
          metadata: PropTypes.shape({
            allowComment: PropTypes.bool,
          }),
        })
      ),
      mapLayers: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string.isRequired,
          geojson: PropTypes.shape({}).isRequired,
        })
      ),
      coordinates: PropTypes.arrayOf(
        PropTypes.shape({
          lat: PropTypes.number.isRequired,
          lng: PropTypes.number.isRequired,
          comment: PropTypes.string,
          language: PropTypes.string.isRequired,
          translatedComment: PropTypes.string,
          createdAt: PropTypes.string.isRequired,
        })
      ).isRequired,
      mapLayersCommentStatistic: PropTypes.arrayOf(
        PropTypes.shape({
          ndoc: PropTypes.number,
          nentry: PropTypes.number,
          word: PropTypes.string,
        })
      ),
      mapLayersSentimentCounts: PropTypes.arrayOf(
        PropTypes.shape({
          label: PropTypes.string,
          mapLayerId: PropTypes.string,
          subtotal: PropTypes.number,
          total: PropTypes.number,
        })
      ),
      pinOptionsCommentStatistic: PropTypes.arrayOf(
        PropTypes.shape({
          ndoc: PropTypes.number,
          nentry: PropTypes.number,
          word: PropTypes.string,
        })
      ),
      pinOptionsSentimentCounts: PropTypes.arrayOf(
        PropTypes.shape({
          inputId: PropTypes.string,
          label: PropTypes.string,
          subtotal: PropTypes.number,
          total: PropTypes.number,
        })
      ),
      overAllCommentStatistic: PropTypes.arrayOf(PropTypes.shape({})),
      overAllSentimentCounts: PropTypes.arrayOf(PropTypes.shape({})),
    }).isRequired,
  }).isRequired,
  hasResponses: PropTypes.bool.isRequired,
};

export default GeolocationQuestionResults;
